// New GPU modules reserve texture units 0, 1, and 2. Everything else stays
// untouched. Extra units can be supplied by a future pipeline extension.
const DEFAULT_TEXTURE_UNITS = [0, 1, 2];
const copy = value => ArrayBuffer.isView(value) ? value.slice() : Array.isArray(value) ? [...value] : value;

/** Snapshot once around an entire render transaction, never around each pass. */
export function snapshotGLState(gl, { textureUnits = DEFAULT_TEXTURE_UNITS } = {}) {
  if (gl.isContextLost()) return { lost: true };
  const parameter = name => copy(gl.getParameter(gl[name]));
  const state = {
    program: parameter('CURRENT_PROGRAM'),
    vao: parameter('VERTEX_ARRAY_BINDING'),
    readFramebuffer: parameter('READ_FRAMEBUFFER_BINDING'),
    drawFramebuffer: parameter('DRAW_FRAMEBUFFER_BINDING'),
    renderbuffer: parameter('RENDERBUFFER_BINDING'),
    viewport: parameter('VIEWPORT'),
    scissorBox: parameter('SCISSOR_BOX'),
    colorMask: parameter('COLOR_WRITEMASK'),
    clearColor: parameter('COLOR_CLEAR_VALUE'),
    depthMask: parameter('DEPTH_WRITEMASK'),
    depthFunc: parameter('DEPTH_FUNC'),
    depthRange: parameter('DEPTH_RANGE'),
    clearDepth: parameter('DEPTH_CLEAR_VALUE'),
    blendColor: parameter('BLEND_COLOR'),
    blendFunc: ['BLEND_SRC_RGB', 'BLEND_DST_RGB', 'BLEND_SRC_ALPHA', 'BLEND_DST_ALPHA'].map(parameter),
    blendEquation: ['BLEND_EQUATION_RGB', 'BLEND_EQUATION_ALPHA'].map(parameter),
    frontFace: parameter('FRONT_FACE'),
    cullFace: parameter('CULL_FACE_MODE'),
    lineWidth: parameter('LINE_WIDTH'),
    polygonOffsetFactor: parameter('POLYGON_OFFSET_FACTOR'),
    polygonOffsetUnits: parameter('POLYGON_OFFSET_UNITS'),
    sampleCoverageValue: parameter('SAMPLE_COVERAGE_VALUE'),
    sampleCoverageInvert: parameter('SAMPLE_COVERAGE_INVERT'),
    clearStencil: parameter('STENCIL_CLEAR_VALUE'),
    activeTexture: parameter('ACTIVE_TEXTURE'),
    capabilities: {},
    buffers: {},
    pixelStore: {},
    textures: [],
  };
  for (const name of ['BLEND', 'DEPTH_TEST', 'STENCIL_TEST', 'SCISSOR_TEST', 'CULL_FACE',
    'RASTERIZER_DISCARD', 'DITHER', 'POLYGON_OFFSET_FILL', 'SAMPLE_ALPHA_TO_COVERAGE', 'SAMPLE_COVERAGE']) {
    state.capabilities[name] = gl.isEnabled(gl[name]);
  }
  for (const [target, binding] of [
    ['ARRAY_BUFFER', 'ARRAY_BUFFER_BINDING'], ['COPY_READ_BUFFER', 'COPY_READ_BUFFER_BINDING'],
    ['COPY_WRITE_BUFFER', 'COPY_WRITE_BUFFER_BINDING'], ['UNIFORM_BUFFER', 'UNIFORM_BUFFER_BINDING'],
    ['PIXEL_PACK_BUFFER', 'PIXEL_PACK_BUFFER_BINDING'], ['PIXEL_UNPACK_BUFFER', 'PIXEL_UNPACK_BUFFER_BINDING'],
  ]) state.buffers[target] = parameter(binding);
  for (const name of ['PACK_ALIGNMENT', 'PACK_ROW_LENGTH', 'PACK_SKIP_PIXELS', 'PACK_SKIP_ROWS',
    'UNPACK_ALIGNMENT', 'UNPACK_ROW_LENGTH', 'UNPACK_IMAGE_HEIGHT', 'UNPACK_SKIP_PIXELS',
    'UNPACK_SKIP_ROWS', 'UNPACK_SKIP_IMAGES', 'UNPACK_FLIP_Y_WEBGL', 'UNPACK_PREMULTIPLY_ALPHA_WEBGL',
    'UNPACK_COLORSPACE_CONVERSION_WEBGL']) state.pixelStore[name] = parameter(name);
  for (const [face, prefix] of [['front', 'STENCIL'], ['back', 'STENCIL_BACK']]) {
    state[face] = {
      writeMask: parameter(`${prefix}_WRITEMASK`),
      func: parameter(`${prefix}_FUNC`),
      ref: parameter(`${prefix}_REF`),
      valueMask: parameter(`${prefix}_VALUE_MASK`),
      fail: parameter(`${prefix}_FAIL`),
      depthFail: parameter(`${prefix}_PASS_DEPTH_FAIL`),
      depthPass: parameter(`${prefix}_PASS_DEPTH_PASS`),
    };
  }
  const units = new Set([...textureUnits, state.activeTexture - gl.TEXTURE0]);
  try {
    for (const unit of units) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      state.textures.push({
        unit,
        texture2D: parameter('TEXTURE_BINDING_2D'),
        textureCube: parameter('TEXTURE_BINDING_CUBE_MAP'),
        texture3D: parameter('TEXTURE_BINDING_3D'),
        textureArray: parameter('TEXTURE_BINDING_2D_ARRAY'),
        sampler: parameter('SAMPLER_BINDING'),
      });
    }
  } finally {
    gl.activeTexture(state.activeTexture);
  }
  return state;
}

/** Restore actual GL bindings and values; Pixi can invalidate its cache after. */
export function restoreGLState(gl, state) {
  if (!state || state.lost || gl.isContextLost()) return;
  gl.useProgram(state.program);
  // Element-array bindings and attribute layouts belong to the host VAO.
  // Binding it restores those without mutating the host's attribute buffers.
  gl.bindVertexArray(state.vao);
  for (const [target, buffer] of Object.entries(state.buffers)) gl.bindBuffer(gl[target], buffer);
  gl.bindRenderbuffer(gl.RENDERBUFFER, state.renderbuffer);
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, state.readFramebuffer);
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, state.drawFramebuffer);
  gl.viewport(...state.viewport);
  gl.scissor(...state.scissorBox);
  gl.colorMask(...state.colorMask);
  gl.clearColor(...state.clearColor);
  gl.depthMask(state.depthMask);
  gl.depthFunc(state.depthFunc);
  gl.depthRange(...state.depthRange);
  gl.clearDepth(state.clearDepth);
  gl.blendColor(...state.blendColor);
  gl.blendFuncSeparate(...state.blendFunc);
  gl.blendEquationSeparate(...state.blendEquation);
  gl.frontFace(state.frontFace);
  gl.cullFace(state.cullFace);
  gl.lineWidth(state.lineWidth);
  gl.polygonOffset(state.polygonOffsetFactor, state.polygonOffsetUnits);
  gl.sampleCoverage(state.sampleCoverageValue, state.sampleCoverageInvert);
  gl.clearStencil(state.clearStencil);
  for (const [faceName, face] of [['front', gl.FRONT], ['back', gl.BACK]]) {
    const stencil = state[faceName];
    gl.stencilMaskSeparate(face, stencil.writeMask);
    gl.stencilFuncSeparate(face, stencil.func, stencil.ref, stencil.valueMask);
    gl.stencilOpSeparate(face, stencil.fail, stencil.depthFail, stencil.depthPass);
  }
  for (const [name, value] of Object.entries(state.pixelStore)) gl.pixelStorei(gl[name], value);
  for (const entry of state.textures) {
    gl.activeTexture(gl.TEXTURE0 + entry.unit);
    gl.bindTexture(gl.TEXTURE_2D, entry.texture2D);
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, entry.textureCube);
    gl.bindTexture(gl.TEXTURE_3D, entry.texture3D);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, entry.textureArray);
    gl.bindSampler(entry.unit, entry.sampler);
  }
  gl.activeTexture(state.activeTexture);
  for (const [name, enabled] of Object.entries(state.capabilities)) {
    if (enabled) gl.enable(gl[name]);
    else gl.disable(gl[name]);
  }
}

/**
 * Warm clears only touch the draw framebuffer, color writes/clear color, and
 * the scissor/rasterizer enables. Capture those without touching texture units
 * or querying any shader, buffer, stencil, viewport, or pixel-store state.
 */
export function snapshotClearState(gl) {
  if (gl.isContextLost()) return { lost: true };
  return {
    drawFramebuffer: gl.getParameter(gl.DRAW_FRAMEBUFFER_BINDING),
    colorMask: copy(gl.getParameter(gl.COLOR_WRITEMASK)),
    clearColor: copy(gl.getParameter(gl.COLOR_CLEAR_VALUE)),
    scissor: gl.isEnabled(gl.SCISSOR_TEST),
    rasterizerDiscard: gl.isEnabled(gl.RASTERIZER_DISCARD),
  };
}

export function restoreClearState(gl, state) {
  if (!state || state.lost || gl.isContextLost()) return;
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, state.drawFramebuffer);
  gl.colorMask(...state.colorMask);
  gl.clearColor(...state.clearColor);
  if (state.scissor) gl.enable(gl.SCISSOR_TEST); else gl.disable(gl.SCISSOR_TEST);
  if (state.rasterizerDiscard) gl.enable(gl.RASTERIZER_DISCARD); else gl.disable(gl.RASTERIZER_DISCARD);
}
